/**
 * The product vocabulary, in one place. README.md is the source of truth:
 * star / constellation / sky / check / shell / spec / stardust / relight / goal.
 * If a name here drifts from the README, the README wins.
 */
/** A subject grouping of stars. Constellation ids are stable; names are copy. */
export const CATEGORIES = {
    sky: { name: "Sky & Space", color: "#4CD7F0", emoji: "🌌" },
    earth: { name: "Our Planet", color: "#7BE8A8", emoji: "🌋" },
    body: { name: "Your Body", color: "#FF6FB5", emoji: "🫀" },
    machines: { name: "How Things Work", color: "#FF9F45", emoji: "✈️" },
    numbers: { name: "Numbers", color: "#B79BFF", emoji: "🔢" },
    animals: { name: "Animals", color: "#FFC94A", emoji: "🐈" },
    words: { name: "Words & Stories", color: "#FFB3C7", emoji: "📖" },
    people: { name: "People & Places", color: "#9BE7FF", emoji: "🗺️" },
};
export const CATEGORY_IDS = Object.keys(CATEGORIES);
/**
 * The mark on a star that has dimmed past the fading line.
 *
 * Defined here, once, because it used to be a literal in the sky and the `body`
 * category's emoji was the same glyph — so "Your Body" wore a permanent 💤 and
 * read as a constellation that could never be woken up, whatever the child did.
 * A symbol in this app means one thing. `no category wears the fading mark` in
 * the vocab tests keeps it that way.
 */
export const FADING_MARK = "💤";
export function isCategoryId(value) {
    return Object.hasOwn(CATEGORIES, value);
}
/**
 * Grade bands drive answer reading level and check difficulty. A child's grade
 * grows on its own each school year (see gradeNow in grade.ts), so the band a
 * child sits in moves with them without anyone editing a profile.
 */
export const GRADE_BANDS = {
    early: { label: "Grades K–2", grades: [0, 1, 2], readingAge: 6 },
    middle: { label: "Grades 3–5", grades: [3, 4, 5], readingAge: 9 },
    upper: { label: "Grades 6–8", grades: [6, 7, 8], readingAge: 12 },
};
export const GRADE_BAND_IDS = Object.keys(GRADE_BANDS);
/** Kindergarten is 0. Grade 8 is the top of what this product targets. */
export const MIN_GRADE = 0;
export const MAX_GRADE = 8;
export function bandForGrade(grade) {
    if (grade <= 2)
        return "early";
    if (grade <= 5)
        return "middle";
    return "upper";
}
/**
 * How many children one grown-up's account can carry.
 *
 * This is the ceiling that stops the others being decorative. A sibling joining
 * an already-consented parent is consented immediately — no second email, no
 * second link, which is right for a real family and was the cheapest way around
 * every other limit we had. One verified mailbox bought unlimited children, each
 * with a full day's allowance of its own: about $1,100 a day of model calls from
 * a single address, going around the email gate rather than through it.
 *
 * Five, because the limit has to be wrong for somebody and it should be wrong
 * in the direction of a rare large family rather than a common script. A sixth
 * child is a support conversation; a sixth thousand is an attack.
 */
export const FAMILY_LIMITS = {
    childrenPerParent: 5,
};
/**
 * Ceilings on ASKING, as opposed to earning.
 *
 * These exist because `/api/ask` is the one route that spends real money, and
 * an authenticated session had no ceiling at all: one token could drive
 * unlimited model calls, whether by a script or by a retry loop in our own
 * client. Authentication decides *who* may spend; this decides *how much*.
 *
 * Both numbers are abuse ceilings, not the product's allowance. A child does
 * not ask sixty questions in a day, and cannot ask eight in a minute when each
 * answer takes about eleven seconds — so a real family should never meet
 * either. The lower, per-tier number that a subscription actually sells belongs
 * to `docs/subscription.md` and is deliberately not decided here: mixing "what
 * you paid for" into "what stops a bot" gives you one number that does neither
 * job well.
 */
export const ASK_LIMITS = {
    /**
     * Questions per calendar day, per child. Counts every ask that reached the
     * model — a blocked one costs a classifier call just like an answered one,
     * so exempting it would leave the cheapest way to spend our money uncapped.
     */
    dailyQuestions: 60,
    /**
     * Burst ceiling per session. Generous on purpose: the day's ceiling is what
     * bounds the spend, and this only stops a retry loop from burning the whole
     * allowance in three seconds or firing sixty calls at the provider at once.
     *
     * It was 8 first, which is still far more than a child can reach when each
     * answer takes about eleven seconds — but it broke the smoke suite, which
     * makes fourteen asks on one token in a few seconds. A limit our own tests
     * trip is a limit that will trip somebody's afternoon.
     */
    perMinute: 20,
};
/**
 * Stardust rules. These live server-side and only server-side — the client
 * displays a balance it is told, it never computes one. Asking a question is
 * deliberately absent from this table and there is no code path that awards
 * for it.
 */
export const STARDUST = {
    /** Finished the check on a topic that was not yet a star. */
    newStar: 15,
    /** Brought a FADING star back. Review is the behaviour worth paying for. */
    relight: 10,
    /**
     * Practised a star that was still bright.
     *
     * Small, and deliberately not zero. Redoing a check you already know is real
     * work and must never read as punished — but it is not the review the
     * schedule was asking for, and paying it the full relight rate made
     * re-practising the same star a farm worth 10 a go up to the daily cap.
     * "Practise all in this thread" turned that into one tap.
     */
    practice: 3,
    /** Got it wrong but stayed and finished. Never zero — finishing is the point. */
    tried: 5,
    /**
     * Extra per level of depth, on top of the base award, when a check is
     * finished on a follow-up question. Paid for FINISHING, never for asking —
     * otherwise "dig deeper" is a farm a nine-year-old finds in an afternoon.
     */
    depthBonus: 5,
    /** Depth beyond this stops paying more. Keeps a long thread from ballooning. */
    maxRewardedDepth: 4,
    /** Ceiling per calendar day, so a Saturday marathon can't buy the big goal. */
    dailyCap: 60,
};
/**
 * What a completed check is worth, given how deep the child had gone. Base
 * award plus a bounded bonus — and the daily cap still applies on top, so depth
 * changes the shape of a day's earning without changing its ceiling.
 */
export function awardFor(input) {
    if (input.isNew) {
        const base = input.correct ? STARDUST.newStar : STARDUST.tried;
        return base + depthLevels(input.depth) * STARDUST.depthBonus;
    }
    /*
      A review only pays the review rate when it WAS a review. The decay curve
      already takes this view — `nextStability` gives its late bonus only to a
      star that had properly faded — and the economy used to disagree with it,
      paying the same 10 whether the star was at 3% or 100%.
  
      No depth bonus here either: at up to +15 it would swamp the base and hand
      back the farm through the other door.
    */
    if (input.fading === false)
        return STARDUST.practice;
    return STARDUST.relight + depthLevels(input.depth) * STARDUST.depthBonus;
}
function depthLevels(depth) {
    return Math.min(Math.max(depth, 1), STARDUST.maxRewardedDepth) - 1;
}
export const STARDUST_REASON_COPY = {
    new_star: "new star lit",
    relight: "you brought a star back",
    practice: "you practised a bright star",
    tried: "you finished the check",
    goal_approved: "traded for your goal",
    goal_returned: "your stardust came back",
    adjustment: "a grown-up changed your stardust",
};
/** Avatars a kid picks at sign-up. Ids are stored; emoji is presentation. */
export const AVATARS = [
    { id: "fox", emoji: "🦊" },
    { id: "cat", emoji: "🐈" },
    { id: "owl", emoji: "🦉" },
    { id: "frog", emoji: "🐸" },
    { id: "whale", emoji: "🐋" },
    { id: "dino", emoji: "🦕" },
    { id: "bee", emoji: "🐝" },
    { id: "octopus", emoji: "🐙" },
    { id: "penguin", emoji: "🐧" },
    { id: "unicorn", emoji: "🦄" },
    { id: "rocket", emoji: "🚀" },
    { id: "comet", emoji: "☄️" },
];
export const AVATAR_IDS = AVATARS.map((a) => a.id);
/** Preset goals a kid can pick from. The parent sets the price, never the kid. */
export const DEFAULT_REWARDS = [
    { label: "15 more minutes of iPad", cost: 60, emoji: "📱" },
    { label: "You pick dinner", cost: 120, emoji: "🍕" },
    { label: "Ice cream trip", cost: 200, emoji: "🍦" },
    { label: "A trip to the park", cost: 250, emoji: "🌳" },
    { label: "Stay up 30 minutes late", cost: 300, emoji: "🌙" },
    { label: "A new book", cost: 400, emoji: "📚" },
    { label: "The LEGO set", cost: 800, emoji: "🧱" },
];
//# sourceMappingURL=vocab.js.map